import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";
import { InstallAppSheet } from "@/components/install/InstallAppSheet";
import { getPlatform, isStandalone } from "@/lib/utils/pwa";

const STORAGE_DISMISSED = "kd_install_hint_dismissed";

/**
 * Dyskretny pasek na ekranie logowania: "Sianko działa lepiej jako apka →".
 * Pokazuje się tylko gdy:
 *  - apka NIE jest już zainstalowana (`isStandalone() === false`)
 *  - platforma to mobile (iOS / Android — desktop pomijamy żeby nie dodawać
 *    szumu na "raz w życiu" instalacji rodzica na komputerze)
 *  - user nie odrzucił go wcześniej (localStorage flag, na zawsze)
 *
 * Anti-dark-pattern: jeden dismiss = na zawsze. Bez liczników "X dni do…",
 * bez powracania bannera.
 */
export function InstallHintBar() {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    if (getPlatform() === "desktop") return;
    if (localStorage.getItem(STORAGE_DISMISSED) === "1") return;
    setShow(true);
  }, []);

  if (!show) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(STORAGE_DISMISSED, "1");
    setShow(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mb-3 rounded-[12px] border border-accent/25 bg-accent-soft px-3.5 py-2.5 text-left text-sm flex items-center gap-2.5 hover:border-accent/40 transition-colors"
      >
        <Smartphone className="h-4 w-4 text-accent shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-ink leading-tight">
            Sianko działa lepiej jako apka
          </span>
          <span className="block text-[11px] text-muted leading-tight">
            Zobacz jak zainstalować
          </span>
        </span>
        <span
          role="button"
          aria-label="Schowaj"
          onClick={handleDismiss}
          className="p-1 -mr-1 text-muted hover:text-ink shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </span>
      </button>

      <InstallAppSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}
